<template>
  <StandardTemplate>
    <h2 class="heading">
      Admin Dashboard
    </h2>
    <div class="grid grid-cols-2  gap-y-4 lg:gap-y-0 lg:gap-x-4 xl:max-w-4xl">
      <!-- banners for prompting the user to upgrade, update information -->
      <div class="text-2xl text-center lg:text-left lg:text-lg col-span-2">
        <HeaderBanner
          :loading="loading"
          :subscription="subscription"
          @toggleSubscriptionModalEvent="toggleSubscriptionModal"
          @toggleBillingModalEvent="toggleBillingModal"
          @reactivateSubscription="reactivateSubscription" />
      </div>
      <!-- left Box: Plan Details display -->
      <BaseBox title="Plan Details" class="lg:min-h-130 col-span-2 lg:col-span-1 flex flex-col">
        <BaseLoading v-if="loading" class="m-auto" />
        <div v-else-if="subscription?.product !== undefined" class="flex-grow flex flex-col">
          <div class="mt-6 mb-3">
            <div class="mb-6 flex justify-center">
              <div v-if="isNewProduct" class="lg:h-40 lg: lg:max-w-[22rem] mx-auto">
                <BaseDog :dog="subscription.product.metadata.image_label" />
              </div>
              <div v-else class="sm:w-40 sm:h-40">
                <BaseDog :dog="subscription.product.metadata.image_label" />
              </div>
            </div>
            <div class="px-2">
              <h3 class="my-4 text-4xl lg:text-2xl">
                <template v-if="$store.getters.isTrialing || $store.getters.isTrialEnded">
                  Trialing
                </template>
                {{ subscription.product.name }}:
              </h3>
              <span class="text-3xl lg:text-xl">
                {{ subscription.product.description }}
                <template v-if="$store.getters.isTrialing || $store.getters.isTrialEnded">Trial </template>
              </span>
            </div>
          </div>

          <!-- billing discription block -->
          <div class="flex-grow pb-4 px-2">
            <!-- next billing at -->
            <div v-if="!userPerms.billing_management" class="px-4 text-center">
              You don't have the required permissions to edit billing and payment information
            </div>
            <!-- if (on paid subscription || active) -->
            <div v-else-if="$store.getters.isActive" id="billing-message-paying"
                 class="flex-col flex mb-4">
              <div>
                <p v-if="subscription.current_billing_amount_in_cents == 0" class="text-2xl lg:text-sm">
                  {{ chargeMessage }}
                </p>
                <p class="text-2xl lg:text-sm">
                  {{ paymentMessage }}
                </p>
                <p class="text-2xl lg:text-sm">
                  scheduled for
                  {{ nextBillingDate }}
                </p>
              </div>
            </div>
            <!-- Plan Details messaging for different subscription states -->
            <div class="flex-col flex">
              <div class="text-2xl lg:text-xl font-semibold">
                <span v-if="$store.getters.isTrialing" id="billing-message-trialing"
                      class="py-3">
                  Trial Ends
                  {{ nextBillingDate }}
                </span>
                <span v-else-if="$store.getters.isTrialEnded" id="billing-message-ended"
                      class="text-error py-3">
                  Trial Ended
                </span>
                <span v-else-if="$store.getters.isCancelled" id="billing-message-cancelled"
                      class="text-error py-3">
                  Subscription Cancelled
                </span>
                <span v-else-if="$store.getters.isPastDue" id="billing-message-past-due"
                      class="text-error py-3">
                  Payment Past Due <br />
                  Update Payment Information
                </span>
              </div>
            </div>
          </div>

          <!-- bottom of Left box: button div  -->
          <div class="flex justify-center mt-6 sm:mt-0 mb-4 px-2">
            <RouterLink
              v-if="$store.getters.isActive || $store.getters.isCancelled || $store.getters.isPastDue"
              v-slot="{navigate}"
              to="/subscription"
              custom>
              <button id="billing-button"
                      class="btn-secondary lg:text-base w-full xl:w-5/6 py-4"
                      :disabled="!userPerms.billing_management ?? undefined"
                      @click="navigate">
                Edit Billing and Payment Info
              </button>
            </RouterLink>
            <button v-else id="upgrade-button"
                    class="btn-primary w-full lg:w-4/5 py-4 lg:text-base border-2 border-main-dark hover:border-main"
                    @click="toggleSubscriptionModal">
              Upgrade Now
            </button>
          </div>
        </div>
      </BaseBox>
      <!-- Right Box: Usage details display, expands to size of adjacent box  -->
      <BaseBox title="Usage Details" class="lg:min-h-130 col-span-2 lg:col-span-1 lg:flex lg:flex-col relative">
        <BaseLoading v-if="loading" class="m-auto" />
        <div v-else-if="subscription.product !== undefined" class="flex-grow flex flex-col">
          <div class="mb-4">
            <DoughnutChart v-if="chartData !== undefined" class="chart h-32 w-32" :chartData="chartData" />
          </div>
          <div class="legend flex-grow pb-4">
            <h3 :class="[exceededInvites > 0 ? 'text-error' : '', 'text-4xl lg:text-2xl']">
              <span v-if="nonDictationUsers > 0"> {{ nonDictationUsers }} + {{ dictationUsers }} / {{ subscription.product.number_of_users }} Users </span>
              <span v-else> {{ dictationUsers }} / {{ subscription.product.number_of_users }} Users </span>
            </h3>
            <div class="legend-item">
              <div class="inline-flex">
                <div class="enabled"></div>
                Enabled Users
              </div>
              {{ enabledUsers }}
            </div>

            <div class="legend-item">
              <div class="inline-flex">
                <div class="pending"></div>
                Pending Invites
              </div>
              {{ pendingUsers }}
            </div>

            <div class="legend-item">
              <div class="inline-flex">
                <div class="remaining"></div>
                Remaining Slots
              </div>
              {{ remainingSlots }}
            </div>
            <div v-if="nonDictationUsers > 0" class="legend-item">
              <div class="inline-flex">
                <div class="managers"></div>
                Manager Seats Filled
              </div>
              {{ nonDictationUsers }}
            </div>
            <div v-if="exceededInvites > 0" class="legend-item">
              <div class="inline-flex">
                <div class="exceeded-invites"></div>
                Exceeded Invite Limit
              </div>
              {{ exceededInvites }}
            </div>
          </div>
          <!--Right Box: bottom button -->
          <div class="flex justify-center mt-6 sm:mt-0 mb-4 px-2">
            <RouterLink v-slot="{navigate}"
                        to="/user-management"
                        custom>
              <button :class="[!userPerms.user_management ? 'disabled' : '', 'btn-secondary lg:text-base w-full xl:w-4/5 py-4']"
                      @click="navigate">
                Manage Users
              </button>
            </RouterLink>
          </div>
        </div>
      </BaseBox>
    </div>

    <UpdateModals v-if="userPerms.billing_management && !loadingProduct"
                  :loading="loading"
                  :productList="productList"
                  :subscription="subscription"
                  :countryList="countryList"

                  :subscriptionModalDisplay="subscriptionModalDisplay"
                  :billingModalDisplay="billingModalDisplay"
                  :threeDSecureUpgradeModalDisplay="threeDSecureUpgradeModalDisplay"

                  @subscriptionUpdate="subscriptionUpdate"
                  @toggleSubscriptionModal="toggleSubscriptionModal"
                  @toggleBillingModal="toggleBillingModal"
                  @toggleThreeDSecureModal="toggleThreeDSecureModal"
                  @pendingRequest="pendingRequest"
                  @setLoading="setLoading"
    />
  </StandardTemplate>
</template>

<script src="./AdminDashboard.js"></script>
<style src="./AdminDashboard.css" scoped></style>